/**
 * Typography System — ClubSwap Brand v1.0
 *
 * Display / headings: Lora (serif) — warm, authoritative
 * Body / UI:          Poppins (sans-serif) — clean, readable on all screen sizes
 *
 * Type hierarchy (from brand book):
 *   H1  — Campaign heading  (Lora Regular, 48px)
 *   H2  — Section title     (Lora Regular, 32px)
 *   H3  — Card title        (Poppins SemiBold, 24px)
 *   H4  — Sub-heading       (Poppins SemiBold, 20px)
 *   H5  — Small heading     (Poppins SemiBold, 18px)
 *   Body — Standard text    (Poppins Regular, 15px)
 *   Caption / Label         (Poppins SemiBold, 12px uppercase)
 *
 * Design v2 editorial scale (the composites at the bottom of this file):
 *   display / displayMobile  — Lora 44 / 30, the Home greeting on the green band
 *   title                    — Lora 36, the page heading (PageBand, PageTitle)
 *   section / sectionMobile  — Lora 24 / 19, the editorial SectionHeader title
 *   pageTitle / detailTitle  — Lora 24 / 20, mobile headers
 *   body / link / buttonText — Poppins 15
 *
 * Color usage (design v2):
 *   Bosgroen     — headings, prices, labels on light backgrounds
 *   Accent (slate) — fills only (primary button, FAB, badges); accent TEXT is Colors.accentStrong
 *   Klaverengroen — captions, section icons on light backgrounds
 *   Saliemist    — text on dark backgrounds
 */

import { TextStyle } from 'react-native';
import { Fonts } from './fonts';

export const FontSizes = {
  xs:   12,
  sm:   14,
  base: 16,
  lg:   18,
  xl:   20,
  '2xl': 24,
  '3xl': 32,
  '4xl': 36,
  '5xl': 48,
  '6xl': 64,
} as const;

export const FontWeights = {
  light:    '300',
  normal:   '400',
  medium:   '500',
  semibold: '600',
  bold:     '700',
} as const;

export const LineHeights = {
  tight:   1.2,
  normal:  1.5,
  relaxed: 1.75,
} as const;

/**
 * Lora's ascender + descender span ~1.25em, so band headings use that ratio:
 * a tighter line box clips descenders ("g", "j") wherever the text is cut to
 * one line (`numberOfLines` on web sets overflow hidden).
 */
const LORA_HEADING_LINE_HEIGHT = 1.25;

/** Band display heading (Home greeting on the green band): Lora 44 / 1.25 */
const DISPLAY: TextStyle = {
  fontFamily:    Fonts.display,
  fontSize:      44,
  fontWeight:    FontWeights.normal,
  lineHeight:    44 * LORA_HEADING_LINE_HEIGHT,
  letterSpacing: -0.5,
};

/** Band display heading on mobile: Lora 30 / 1.25 */
const DISPLAY_MOBILE: TextStyle = {
  fontFamily:    Fonts.display,
  fontSize:      30,
  fontWeight:    FontWeights.normal,
  lineHeight:    30 * LORA_HEADING_LINE_HEIGHT,
  letterSpacing: -0.5,
};

export const Typography: Record<string, TextStyle> = {
  // ── Display headings (Lora) ──────────────────────────────────────────────
  h1: {
    fontFamily: Fonts.display,
    fontSize:   FontSizes['5xl'],   // 48px — campaign heading
    fontWeight: FontWeights.normal,
    lineHeight: FontSizes['5xl'] * LineHeights.tight,
  },
  h2: {
    fontFamily: Fonts.display,
    fontSize:   FontSizes['3xl'],   // 32px — section title
    fontWeight: FontWeights.normal,
    lineHeight: FontSizes['3xl'] * LineHeights.tight,
  },

  // ── UI headings (Poppins) ────────────────────────────────────────────────
  h3: {
    fontFamily: Fonts.semiBold,
    fontSize:   FontSizes['2xl'],   // 24px — card title
    fontWeight: FontWeights.semibold,
    lineHeight: FontSizes['2xl'] * LineHeights.tight,
  },
  h4: {
    fontFamily: Fonts.semiBold,
    fontSize:   FontSizes.xl,       // 20px — sub-heading
    fontWeight: FontWeights.semibold,
    lineHeight: FontSizes.xl * LineHeights.normal,
  },
  h5: {
    fontFamily: Fonts.semiBold,
    fontSize:   FontSizes.lg,       // 18px — small heading
    fontWeight: FontWeights.semibold,
    lineHeight: FontSizes.lg * LineHeights.normal,
  },

  // ── Body text (Poppins) ──────────────────────────────────────────────────
  bodyLarge: {
    fontFamily: Fonts.regular,
    fontSize:   FontSizes.lg,
    fontWeight: FontWeights.normal,
    lineHeight: FontSizes.lg * LineHeights.normal,
  },
  /** Standard body copy (design system `body`): Poppins 15 / 1.5 */
  body: {
    fontFamily: Fonts.regular,
    fontSize:   15,
    fontWeight: FontWeights.normal,
    lineHeight: 15 * LineHeights.normal,
  },
  bodySmall: {
    fontFamily: Fonts.regular,
    fontSize:   FontSizes.sm,
    fontWeight: FontWeights.normal,
    lineHeight: FontSizes.sm * LineHeights.normal,
  },

  // ── Caption / label (Poppins, uppercase per brand book) ─────────────────
  caption: {
    fontFamily:      Fonts.semiBold,
    fontSize:        FontSizes.xs,  // 12px
    fontWeight:      FontWeights.semibold,
    lineHeight:      FontSizes.xs * LineHeights.normal,
    letterSpacing:   0.8,
    textTransform:   'uppercase',
  },

  // ── Special UI styles ────────────────────────────────────────────────────
  /** Button label (design system `button`): Poppins semibold 15 */
  buttonText: {
    fontFamily:  Fonts.semiBold,
    fontSize:    15,
    fontWeight:  FontWeights.semibold,
    letterSpacing: 0.3,
  },
  /** Text link ("View all →"): Poppins semibold 15, always Colors.accentStrong */
  link: {
    fontFamily: Fonts.semiBold,
    fontSize:   15,
    fontWeight: FontWeights.semibold,
  },
  label: {
    fontFamily:    Fonts.semiBold,
    fontSize:      FontSizes.sm,
    fontWeight:    FontWeights.semibold,
    letterSpacing: 0.3,
  },

  // ── Display italic variant (Lora Italic — for brand accents) ────────────
  displayItalic: {
    fontFamily: Fonts.displayItalic,
    fontSize:   FontSizes['3xl'],
    fontWeight: FontWeights.normal,
    lineHeight: FontSizes['3xl'] * LineHeights.tight,
  },

  // ── Design v2 composite styles (Lora is regular weight only) ─────────────
  /** Band display heading (Home greeting on the green band): Lora 44 / 1.25 */
  display: DISPLAY,
  /** Band display heading on mobile: Lora 30 / 1.25 */
  displayMobile: DISPLAY_MOBILE,
  /** Page heading (PageTitle, the band headline on Browse/Clubs/Messages): Lora 36 / 1.25 */
  title: {
    fontFamily:    Fonts.display,
    fontSize:      FontSizes['4xl'],   // 36
    fontWeight:    FontWeights.normal,
    lineHeight:    FontSizes['4xl'] * LORA_HEADING_LINE_HEIGHT,
    letterSpacing: -0.3,
  },
  /** Mobile header title, profile name: Lora 24 */
  pageTitle: {
    fontFamily: Fonts.display,
    fontSize:   FontSizes['2xl'],
    fontWeight: FontWeights.normal,
    lineHeight: FontSizes['2xl'] * LineHeights.tight,
  },
  /** Detail header title (back variant), chat header: Lora 20 */
  detailTitle: {
    fontFamily: Fonts.display,
    fontSize:   FontSizes.xl,
    fontWeight: FontWeights.normal,
    lineHeight: FontSizes.xl * LineHeights.tight,
  },
  /** Section title, dialog titles, Browse count: Lora 24 / 1.25 */
  section: {
    fontFamily: Fonts.display,
    fontSize:   FontSizes['2xl'],   // 24
    fontWeight: FontWeights.normal,
    lineHeight: FontSizes['2xl'] * 1.25,
  },
  /** Section title on mobile: Lora 19 / 1.25 */
  sectionMobile: {
    fontFamily: Fonts.display,
    fontSize:   19,
    fontWeight: FontWeights.normal,
    lineHeight: 19 * 1.25,
  },
  /** Card title: Poppins semibold 15 */
  cardTitle: {
    fontFamily: Fonts.semiBold,
    fontSize:   15,
    fontWeight: FontWeights.semibold,
    lineHeight: 15 * 1.35,
  },
  /** Price in cards: Poppins bold 16 */
  price: {
    fontFamily: Fonts.bold,
    fontSize:   FontSizes.base,
    fontWeight: FontWeights.bold,
    lineHeight: FontSizes.base * LineHeights.tight,
  },
  /** Meta text (club name, size, timestamps, hints, helper/error text): Poppins 13 */
  meta: {
    fontFamily: Fonts.regular,
    fontSize:   13,
    fontWeight: FontWeights.normal,
    lineHeight: 13 * 1.45,
  },
  /** Overline: Poppins semibold 11 uppercase, .8 tracking */
  overline: {
    fontFamily:    Fonts.semiBold,
    fontSize:      11,
    fontWeight:    FontWeights.semibold,
    lineHeight:    11 * LineHeights.tight,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  /** Numeral stat: Poppins bold 20 */
  statValue: {
    fontFamily: Fonts.bold,
    fontSize:   FontSizes.xl,
    fontWeight: FontWeights.bold,
    lineHeight: FontSizes.xl * LineHeights.tight,
  },
  /** Stat label: Poppins medium 12 */
  statLabel: {
    fontFamily: Fonts.medium,
    fontSize:   FontSizes.xs,
    fontWeight: FontWeights.medium,
    lineHeight: FontSizes.xs * LineHeights.tight,
  },
} as const;

export type FontSizeKey = keyof typeof FontSizes;
export type TypographyKey = keyof typeof Typography;
